import { useState, useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import Field from "@/shared/components/field";
import JsonSchemaEditor from "@/shared/components/json_schema_editor";

interface HttpRequestNodeEditorProps {
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
}

const METHODS = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
];

type HeaderEntry = { key: string; value: string };

function parseHeaders(raw: unknown): HeaderEntry[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  return Object.entries(raw as Record<string, unknown>).map(([key, val]) => ({
    key,
    value: String(val ?? ""),
  }));
}

function headersToObject(entries: HeaderEntry[]): Record<string, string> {
  const obj: Record<string, string> = {};
  for (const e of entries) {
    if (e.key.trim()) obj[e.key.trim()] = e.value;
  }
  return obj;
}

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-green-500/10 text-green-700 border-green-500/20",
  POST: "bg-blue-500/10 text-blue-700 border-blue-500/20",
  PUT: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  PATCH: "bg-orange-500/10 text-orange-700 border-orange-500/20",
  DELETE: "bg-red-500/10 text-red-700 border-red-500/20",
};

export default function HttpRequestNodeEditor({ value, onChange }: HttpRequestNodeEditorProps) {
  const url = String(value.url ?? "");
  const method = String(value.method ?? "GET");
  const timeoutSeconds = value.timeoutSeconds === undefined ? 30 : Number(value.timeoutSeconds);
  const [headers, setHeaders] = useState<HeaderEntry[]>(() => parseHeaders(value.headers));
  const lastEmittedHeaders = useRef(value.headers);
  if (value.headers !== lastEmittedHeaders.current) {
    lastEmittedHeaders.current = value.headers;
    setHeaders(parseHeaders(value.headers));
  }

  const [bodyText, setBodyText] = useState(() => {
    const b = value.body;
    if (b === null || b === undefined) return "";
    return typeof b === "string" ? b : JSON.stringify(b, null, 2);
  });

  const emit = (patch: Partial<Record<string, unknown>>) => {
    onChange({ ...value, ...patch });
  };

  const handleBodyChange = (text: string) => {
    setBodyText(text);
    if (!text.trim()) {
      emit({ body: null });
      return;
    }
    try {
      emit({ body: JSON.parse(text) });
    } catch {
      // keep local text while JSON is invalid
    }
  };

  const emitHeaders = (next: HeaderEntry[]) => {
    setHeaders(next);
    const obj = headersToObject(next);
    lastEmittedHeaders.current = obj;
    emit({ headers: obj });
  };

  const updateHeader = (index: number, patch: Partial<HeaderEntry>) => {
    emitHeaders(headers.map((h, i) => (i === index ? { ...h, ...patch } : h)));
  };

  const addHeader = () => {
    setHeaders((prev) => [...prev, { key: "", value: "" }]);
  };

  const removeHeader = (index: number) => {
    emitHeaders(headers.filter((_, i) => i !== index));
  };

  const needsBody = method !== "GET" && method !== "DELETE";

  return (
    <div className="space-y-4">
      {/* Method pill + URL */}
      <div className="space-y-3">
        <div className="flex items-end gap-2">
          <div className="w-28 shrink-0">
            <Field
              label="Método"
              name="method"
              type="select"
              value={method}
              onChange={(e) => emit({ method: e.target.value })}
              options={METHODS}
            />
          </div>
          <div className="flex-1">
            <Field
              label="URL"
              name="url"
              value={url}
              onChange={(e) => emit({ url: e.target.value })}
              placeholder="https://api.ejemplo.com/endpoint"
            />
          </div>
        </div>

        {/* Method badge */}
        <div
          className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${METHOD_COLORS[method] ?? "bg-gray-500/10 text-gray-700 border-gray-500/20"}`}
        >
          {method}
        </div>
      </div>

      {/* Timeout */}
      <Field
        label="Timeout (segundos)"
        name="timeoutSeconds"
        type="number"
        value={timeoutSeconds}
        onChange={(e) =>
          emit({
            timeoutSeconds: e.target.value === "" ? undefined : Math.max(1, Number(e.target.value)),
          })
        }
        placeholder="30"
      />

      {/* Headers — key/value editor */}
      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wider text-foreground/40">Headers</p>

        {headers.length === 0 && (
          <p className="text-xs text-foreground/30 italic">Sin headers adicionales</p>
        )}

        {headers.map((header, index) => (
          <div key={index} className="flex items-start gap-2">
            <div className="flex-1 grid grid-cols-2 gap-2">
              <input
                type="text"
                value={header.key}
                onChange={(e) => updateHeader(index, { key: e.target.value })}
                placeholder="Header"
                className="w-full bg-white/80 border border-black/5 rounded-xl py-2.5 px-3 text-sm font-medium text-foreground focus:outline-none focus:border-accent/30 focus:ring-4 focus:ring-accent/5 transition-all shadow-sm font-mono"
              />
              <input
                type="text"
                value={header.value}
                onChange={(e) => updateHeader(index, { value: e.target.value })}
                placeholder="Valor"
                className="w-full bg-white/80 border border-black/5 rounded-xl py-2.5 px-3 text-sm font-medium text-foreground focus:outline-none focus:border-accent/30 focus:ring-4 focus:ring-accent/5 transition-all shadow-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => removeHeader(index)}
              className="mt-2.5 p-1.5 rounded-full hover:bg-red-500/10 hover:text-red-500 text-foreground/30 transition-colors shrink-0"
              aria-label="Eliminar header"
            >
              <Trash2 className="size-3.5" />
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addHeader}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#0071e3] hover:text-[#0077ed] transition-colors pt-1"
        >
          <Plus className="size-3.5" />
          Agregar header
        </button>
      </div>

      {/* Body */}
      <div>
        {needsBody ? (
          <JsonSchemaEditor
            label="Body (JSON)"
            name="body"
            value={bodyText}
            onChange={handleBodyChange}
            placeholder='{ "key": "value" }'
            rows={6}
          />
        ) : (
          <div className="space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-foreground/40">Body</p>
            <p className="text-xs text-foreground/30 italic">
              {method} no requiere body
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
